import type { ServiceCheckStatus } from '@sunstone/api-client/gen/health/v1/health_pb';
import { useInvestigateService } from '@sunstone/api-client/hooks';
import { Button, DataTable, Dialog, DialogContent, DialogHeader, DialogTitle } from '@sunstone/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useAuthToken } from '@/features/auth';

function timeAgo(isoString: string): string {
  if (!isoString) return '\u2014';
  const now = Date.now();
  const then = new Date(isoString).getTime();
  if (isNaN(then)) return '\u2014';
  const diffMs = now - then;
  if (diffMs < 0) return 'just now';
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

// A suppressed check is expected fallout from a dependency that's already
// down, not a separate incident — treated as healthy for action buttons
// (Page/Investigate) the same way it already is for the status label/color.
function isHealthyStatus(status: string): boolean {
  return status === 'green' || status === 'suppressed' || !status;
}

// Worst-first so the default Status sort surfaces at-risk services - plain
// alphabetical would put 'green' (Healthy) first. red/timeout/error render
// identically in StatusBadge (all destructive) but rank separately here -
// a confirmed failure (red) outranks the more ambiguous couldn't-tell
// outcomes (timeout/error).
// Falls back to the healthy tier for an empty status, matching
// isHealthyStatus above - the only unmapped value that actually occurs.
const STATUS_SORT_RANK: Record<string, number> = {
  red: 0,
  timeout: 1,
  error: 2,
  yellow: 3,
  suppressed: 4,
  green: 5,
};
function statusSortRank(status: string): number {
  return STATUS_SORT_RANK[status] ?? 5;
}

function buildColumns(
  onInvestigate: (workload: string) => void,
  investigatingWorkload: string | undefined,
): ColumnDef<ServiceCheckStatus>[] {
  return [
    {
      id: 'service',
      header: 'Service',
      meta: { widthPercent: 26 },
      // Filters on name, status, or owner, matching the manual search this
      // replaces - the cell below still only ever displays the name.
      accessorFn: (row) => `${row.name} ${row.status} ${row.owner ?? ''}`,
      sortingFn: (rowA, rowB) => rowA.original.name.localeCompare(rowB.original.name),
      cell: ({ row }) => (
        <div className="min-w-0">
          <span className="truncate block font-mono text-sm">{row.original.name}</span>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      meta: { widthPercent: 13 },
      accessorFn: (row) => row.status,
      // Ties (e.g. two 'red' rows) break by service name ascending - baked in
      // here rather than left to a second entry in the table's sorting
      // state, since a plain header click replaces that state with just
      // this column (see DataTable's SortableHeader), which would otherwise
      // drop the name tiebreak and fall back to raw insertion order.
      sortingFn: (rowA, rowB) => {
        const rankDiff =
          statusSortRank(rowA.original.status) - statusSortRank(rowB.original.status);
        return rankDiff !== 0 ? rankDiff : rowA.original.name.localeCompare(rowB.original.name);
      },
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      id: 'lastChecked',
      header: 'Last Checked',
      meta: { widthPercent: 14 },
      enableSorting: false,
      accessorFn: (row) => row.lastChecked,
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{timeAgo(row.original.lastChecked)}</span>
      ),
    },
    {
      id: 'owner',
      header: 'Owner',
      meta: { widthPercent: 22 },
      accessorFn: (row) => row.owner,
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{row.original.owner || 'Unknown'}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      meta: { widthPercent: 25 },
      enableSorting: false,
      cell: ({ row }) => {
        const isHealthy = isHealthyStatus(row.original.status);
        const isInvestigating = investigatingWorkload === row.original.name;
        return (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground hover:text-foreground"
              disabled={isHealthy || isInvestigating}
              onClick={() => onInvestigate(row.original.name)}
            >
              {isInvestigating ? (
                <span className="inline-flex items-center gap-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Investigating...
                </span>
              ) : (
                'Investigate'
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground hover:text-foreground"
              disabled={isHealthy}
              onClick={() => {
                // Placeholder — PagerDuty integration is a deliberate later iteration.
              }}
            >
              Page
            </Button>
          </div>
        );
      },
    },
  ];
}

interface ServiceHealthPanelProps {
  checks?: ServiceCheckStatus[];
  isLoading?: boolean;
}

export function ServiceHealthPanel({ checks, isLoading }: ServiceHealthPanelProps) {
  const [investigateResult, setInvestigateResult] = useState<{
    workload: string;
    rootCause?: string;
    error?: string;
  } | null>(null);
  const { token } = useAuthToken();
  const investigate = useInvestigateService(token);

  const handleInvestigate = useCallback(
    (workload: string) => {
      investigate.mutate(workload, {
        onSuccess: (data) => {
          setInvestigateResult({ workload, rootCause: data.rootCause || 'No root cause found.' });
        },
        onError: (err) => {
          setInvestigateResult({
            workload,
            error: err instanceof Error ? err.message : String(err),
          });
        },
      });
    },
    [investigate.mutate],
  );

  const columns = useMemo(
    () =>
      buildColumns(handleInvestigate, investigate.isPending ? investigate.variables : undefined),
    [handleInvestigate, investigate.isPending, investigate.variables],
  );

  return (
    <>
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-foreground">Service Health</h3>
        <DataTable
          columns={columns}
          data={checks ?? []}
          searchKey="service"
          searchPlaceholder="Search"
          isLoading={isLoading}
          pageSize={1000}
          enableSorting
          initialSorting={[{ id: 'status', desc: false }]}
        />
      </div>
      <Dialog
        open={investigateResult !== null}
        onOpenChange={(open) => !open && setInvestigateResult(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Investigation: {investigateResult?.workload}</DialogTitle>
          </DialogHeader>
          {investigateResult?.error ? (
            <div className="max-h-[60vh] overflow-y-auto text-sm text-red-600 dark:text-red-400 whitespace-pre-wrap">
              Investigation failed: {investigateResult.error}
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto text-sm text-foreground whitespace-pre-wrap">
              {investigateResult?.rootCause}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
